import React, { useMemo } from "react";
import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { JobPageHeader } from "./job-page-components/job-page-header/job-page-header.component";
import { RefContextProvider, useRefContext } from "../../context/RefContext";
import { Loading } from "@/components/src/animations/loading";
import { JobNotFoundComponent } from "./job-page-components/job-not-found.component";
import { useJobStore } from "../../store/Jobs";
import { AcceptJobRequestResult } from "../../store/Jobs/job.store";
import { useCentralStore } from "../../store/Central";
import { CreateJobDocumentFromTemplateDialog } from "./job-page-components/create-job-document-from-template-dialog.component";
import { JobDocumentTypeEnum } from "./job-document/job-document.types";
import { ClientEntityType, JobWithShare } from "@/lib/supabase/supabaseTypes";
import { useJobRequestSheet } from "../../store/Sheets/sheet-hooks/useJobRequestSheet";
import { JobChatPortal } from "./job-chat/JobChatPortal";
import { X } from "lucide-react";
import { Button as ShadcnButton } from "@/dentlab/src/components/ui/button";
import { ConfigurableJobInfoTabs } from "./job-page-components/job-info-tabs.component";
import { JobDocumentTabs } from "./job-page-components/job-document-tabs.component";
import { ChatComponent } from "../../components/chat/chat.component";
import { VerticalResizablePanels } from "../../components/resizable-panels/vertical-resizable-panels.component";
import { SectionForNotConnected } from "../client-chat-page/section-for-not-connect.component";
import { useNavigate } from "react-router-dom";
import { showNotification } from "../../store/Central/selectors";
import { useTabConfigStore } from "../../store/TabConfig/tab-config.store";
import { SYSTEM_DEFAULT_TAB_CONFIG } from "../../config/default-tab-config";
import { parseDensity } from "../../config/density-config";
import { TabConfig } from "../../types/tab-config.types";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";

type JobListOutletContext = {
	isJobListView: boolean;
	setIsJobListView: (value: boolean) => void;
};

/**
 * JobPage - Page displaying information for a single job, accessed via /job/:jobId
 * The job page uses useJobStore to handle all state related to the job, and the corresponding client and patient enttities.
 */
const JobPageComponent: React.FC = () => {
	const outletContext = useOutletContext<JobListOutletContext | undefined>();
	const client = useCentralStore((state) => state.client);
	const clientOrganizationId = useCentralStore(
		(state) => state.clientOrganizationId
	);
	const job = useJobStore((state) => state.job);
	const acceptJobRequest = useJobStore((state) => state.acceptJobRequest);
	const denyJobRequest = useJobStore((state) => state.denyJobRequest);
	const shareFile = useJobStore((state) => state.shareFile);
	const unshareFile = useJobStore((state) => state.unshareFile);
	const childJobChannels = useJobStore((state) => state.childJobChannels);
	const fetchChildJobChannels = useJobStore(
		(state) => state.fetchChildJobChannels
	);
	const fetchJob = useJobStore((state) => state.fetchJob);
	const fetchJobFiles = useJobStore((state) => state.fetchJobFiles);
	const configurations = useTabConfigStore((state) => state.configurations);
	const defaultConfigId = useTabConfigStore((state) => state.defaultConfigId);
	const enableBackgroundDoubleClickFullscreen = useCentralStore(
		(state) =>
			(state.organization?.config as Record<string, unknown> | null)
				?.background_double_click_fullscreen_enabled !== false
	);
	const { openJobRequestSheet } = useJobRequestSheet();
	const navigate = useNavigate();
	const clientId = client?.id;
	const { container } = useRefContext();

	const [openJobDocumentDialog, setOpenJobDocumentDialog] =
		useState<boolean>(false);
	const [chatOpen, setChatOpen] = useState<boolean>(false);
	const [fullscreenPanel, setFullscreenPanel] = useState<
		"top" | "bottom" | null
	>(null);

	const initialPanelHeight = `${getLocalStorage(
		"dentlab-job-panel-height",
		50
	)}%`;

	// When the job is shared, open the chat sidebar (both when opening job and when state changes)
	useEffect(() => {
		setChatOpen(job?.shared_with ? true : false);
	}, [job?.shared_with]);

	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (e.key === "d" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				setOpenJobDocumentDialog((open) => !open);
			}
		};

		document.addEventListener("keydown", down);
		return () => document.removeEventListener("keydown", down);
	}, []);

	const handleOpenVendorDialog = () => {
		const jobId = job?.id ?? null;
		if (!jobId) {
			showNotification({
				message: "Fehler beim Öffnen des Auftrags",
				type: "error",
			});
			return;
		}
		openJobRequestSheet({
			jobTitle: `${job?.title} ${job?.code ? `(${job?.code})` : ""}`,
			jobId: jobId,
			onSubmit: () => {
				setTimeout(() => {
					if (job?.id) {
						fetchChildJobChannels(job.id);
						fetchJob(job.id);
					}
				}, 1000); // TODO: Wait to ensure that all server side actions are completed, improve this
			},
		});
	};

	const handleChildJobChannelClick = (channelId: string) => {
		if (!channelId) return;

		navigate(`/chats/${channelId}`);
	};

	const handleUpdateJobFiles = async () => {
		if (job?.id) {
			fetchJobFiles(job.id);
		}
	};

	const handlePanelHeightChange = useCallback((heightPercent: number) => {
		setLocalStorage("dentlab-job-panel-height", heightPercent);
	}, []);

	const resolvedConfig = useMemo(() => {
		if (client?.default_job_tabs_config_id) {
			return configurations.find(
				(config) => config.id === client?.default_job_tabs_config_id
			);
		}
		if (defaultConfigId) {
			return configurations.find(
				(config) => config.id === defaultConfigId
			);
		}
		return null;
	}, [client?.default_job_tabs_config_id, defaultConfigId]);

	const tabConfig = useMemo(() => {
		return (
			(resolvedConfig?.tabs as unknown as TabConfig[]) ??
			SYSTEM_DEFAULT_TAB_CONFIG
		);
	}, [resolvedConfig]);

	const densitySettings = useMemo(
		() =>
			parseDensity(
				resolvedConfig ?? {
					density_padding: 4,
					density_gap: 2,
					density_input_height: 28,
					density_font_size: 12,
				}
			),
		[resolvedConfig]
	);

	return (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "row",
			}}
			ref={container}
		>
			<div
				style={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				}}
			>
				<div
					style={{
						position: "sticky",
						top: 0,
						zIndex: 10,
					}}
				>
					<JobPageHeader
						chatOpen={chatOpen}
						onToggleChat={() => setChatOpen((open) => !open)}
						isJobListOpen={outletContext?.isJobListView}
						onToggleJobList={
							outletContext
								? () =>
										outletContext.setIsJobListView(
											!outletContext.isJobListView
										)
								: undefined
						}
					/>
				</div>
				<VerticalResizablePanels
					topContent={
						<ConfigurableJobInfoTabs
							isFullscreen={fullscreenPanel === "top"}
							onToggleFullscreen={() =>
								setFullscreenPanel(
									fullscreenPanel === "top" ? null : "top"
								)
							}
							tabConfig={tabConfig}
							densitySettings={densitySettings}
						/>
					}
					bottomContent={
						<JobDocumentTabs
							onOpenVendorDialog={handleOpenVendorDialog}
							childJobChannels={childJobChannels}
							onChildJobChannelClick={handleChildJobChannelClick}
						/>
					}
					initialTopHeight={initialPanelHeight}
					minTopHeight={40}
					minBottomHeight={150}
					fullscreenPanel={fullscreenPanel}
					onHeightChange={handlePanelHeightChange}
					onFullscreenChange={setFullscreenPanel}
					enableBackgroundDoubleClickFullscreen={
						enableBackgroundDoubleClickFullscreen
					}
					className="flex-1 min-h-0"
				/>
			</div>
			<CreateJobDocumentFromTemplateDialog
				open={openJobDocumentDialog}
				setOpen={setOpenJobDocumentDialog}
				templateType={JobDocumentTypeEnum.DeliveryNote}
			/>
			<JobChatPortal isOpen={chatOpen} onToggle={setChatOpen}>
				<div className="h-full py-1 pr-1">
					<div className="h-full bg-white rounded-xl flex flex-col">
						<div className="flex flex-row w-full gap-2 items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
							<div className="font-semibold">Chat & Historie</div>
							<ShadcnButton
								variant="ghost"
								size="icon"
								onClick={() => setChatOpen(false)}
								className="w-5 h-5"
							>
								<X className="w-5 h-5" />
							</ShadcnButton>
						</div>
						{job && (
							<div className="flex-1 min-h-0">
								<JobChat
									job={job}
									client={client}
									clientOrganizationId={clientOrganizationId}
									onShareFile={shareFile}
									onUnshareFile={unshareFile}
									onAcceptJobRequest={acceptJobRequest}
									onDenyJobRequest={denyJobRequest}
									updateJobFiles={handleUpdateJobFiles}
								/>
							</div>
						)}
					</div>
				</div>
			</JobChatPortal>
		</div>
	);
};

const JobChat: React.FC<{
	job: JobWithShare;
	client: ClientEntityType | null;
	clientOrganizationId: string | null;
	onShareFile: (fileId: string) => Promise<void>;
	onUnshareFile: (fileId: string) => Promise<void>;
	onAcceptJobRequest: (
		channelId: string | null
	) => Promise<AcceptJobRequestResult>;
	onDenyJobRequest: (channelId: string | null) => Promise<boolean>;
	updateJobFiles: () => void;
}> = ({
	job,
	client,
	onShareFile,
	onUnshareFile,
	onAcceptJobRequest,
	onDenyJobRequest,
	updateJobFiles,
}) => {
	if (!client?.organization_id || !job.id) {
		return null;
	}
	// To return the connect section we need to both check if the client is not connected
	// AND if the job hasn't been shared before (if the client was once connected)
	// Because channels are accessible as long as you were the vendor or customer org at time of creation, a client
	// can in theory have n channels with different orgs each. Because messages only rely on access to the channel, the channels
	// can be continued to be used indefinitely.
	if (!client?.connected_organization_id && !job.shared_with) {
		return <SectionForNotConnected />;
	}
	return (
		<div className="relative h-full">
			<ChatComponent
				channelId={job.channel_id ?? null}
				organizationId={client?.organization_id}
				onShareFile={onShareFile}
				onUnshareFile={onUnshareFile}
				onAcceptJobRequest={onAcceptJobRequest}
				onDenyJobRequest={onDenyJobRequest}
				updateJobFiles={updateJobFiles}
				allowGoTo={false}
				jobId={job.id}
				vendorOrgId={client?.organization_id}
				customerOrgId={
					client?.connected_organization_id ??
					job?.shared_with ??
					undefined
				}
				checkForClientConnectionToCustomer={false}
			/>
		</div>
	);
};

export const JobPage: React.FC = () => {
	// retrieve jobId from url /job/:jobId
	const { organizationId, clientId } = useCentralStore((state) => ({
		organizationId: state.organization?.id,
		clientId: state.client?.id,
	}));
	const { job, jobLoading } = useJobStore((state) => ({
		job: state.job,
		jobLoading: state.jobLoading,
	}));

	if (!organizationId || jobLoading) {
		return <Loading />;
	}

	if (!job?.id) {
		return <JobNotFoundComponent />;
	}

	if (job && job.client_id !== clientId) {
		return (
			<div>
				Die Referenz des Auftrags zum Auftraggeber stimmt nicht mit dem
				aktuellen Auftraggeber überein. {job.client_id} {clientId}
			</div>
		);
	}
	return (
		<RefContextProvider>
			<JobPageComponent />
		</RefContextProvider>
	);
};
